<script setup lang="ts">
import { useAppStore } from '@/store/app/app'

import type { Category } from '@/types/api/api-v3/category'

type Props = {
  category: Category
}

const props = defineProps<Props>()

const route = useRoute()

const appStore = useAppStore()

const isErrorImage = ref(false)

const productImage = computed(() => {
  if (isErrorImage.value || !props.category.image) {
    return './no-image-category.svg'
  }

  return props.category.image
})
const isCategoryActive = computed(() => {
  return (
    props?.category.slug === appStore.activeCategorySlug &&
    route.path.includes('category')
  )
})

const handleClick = () => {
  appStore.goToCategory(props?.category.slug)
}
</script>

<template>
  <a
    class="main-category-card"
    itemprop="url"
    :class="{
      'main-category-card--active': isCategoryActive,
    }"
    @click.prevent="handleClick"
  >
    <div itemprop="name" class="main-category-card__title">
      {{ category.name }}
    </div>

    <nuxt-img
      :alt="category.slug"
      :src="productImage"
      itemprop="image"
      class="main-category-card__image"
      @error="isErrorImage = true"
    />
  </a>
</template>

<style lang="scss" scoped>
.main-category-card {
  position: relative;
  display: block;
  height: 157px;
  overflow: hidden;
  cursor: pointer;
  background-color: var(--ui-grey);
  border-radius: 16px;
  transition: background-color 0.2s ease-in;

  @include media('>=desktop-mini') {
    height: 112px;
  }

  &:hover:not(.main-category-card--active) {
    background-color: var(--ui-grey--hover);
  }

  &__title {
    position: relative;
    z-index: 1;
    padding: 12px 8px 0;
    font-size: 14px;
    font-weight: 800;
    line-height: 18px;
    color: var(--ui-text-primary);
    transition: color 0.2s ease-in;

    @include media('>=desktop-mini') {
      font-size: 12px;
      line-height: 14px;
    }
  }

  &__image {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    object-fit: contain;
    object-position: bottom right;
  }

  &--active {
    background-color: var(--ui-peach);

    .main-category-card__title {
      color: var(--ui-primary);
    }
  }
}
</style>
